import { useMemo, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { addOutlineSx } from '../../components/addAction';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import TerrainOutlinedIcon from '@mui/icons-material/TerrainOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Link as RouterLink } from 'react-router-dom';
import { linkClick } from '../../components/routerLink';
import type {
    FindingPlanumDocType,
    FindingProfileDocType,
    Subject
} from '@ast-data/shared';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { duplicateSlotKeys, isDuplicateSlot } from './useDescriptionDuplicates';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useFindingDeletion } from './useFindingDeletion';
import DeletionImpactDetails from './DeletionImpactDetails';

import { compareNatural } from '../../utils/compare';

/** One description row reduced to what a list item needs: its id + number. */
interface DescriptionRow {
    id: string;
    number: string;
    /** The Teil mark, appended to the row's label when there is one. */
    partLabel: string;
    /**
     * The number is used by a second description of the same kind under this
     * Befund — only sync can produce that, and it has to be renumbered.
     */
    isDuplicate: boolean;
}

interface KindBlock {
    /** What `useFindingDeletion` calls this kind. */
    kind: 'planum' | 'profile';
    /** URL segment of the editor route (English plural). */
    segment: string;
    /** CASL subject for the create guard on Add and the delete guard per row. */
    subject: Subject;
    titleId: string;
    deleteTitleId: string;
    deleteConfirmId: string;
    addId: string;
    emptyId: string;
    labelId: string;
    icon: ReactNode;
    rows: DescriptionRow[];
}

function byNumber(left: DescriptionRow, right: DescriptionRow): number {
    return (
        compareNatural(left.number, right.number) ||
        // Teile sort under the number they divide.
        compareNatural(left.partLabel, right.partLabel)
    );
}

/**
 * One kind's rows, sorted, with the number collisions flagged. Every row here
 * belongs to the same Befund and the same kind — exactly the scope the
 * uniqueness rule spans — so the list's own entries decide it.
 */
function describedRows(
    entries: readonly { id: string; number: string; partLabel: string }[]
): DescriptionRow[] {
    const twice = duplicateSlotKeys(entries);
    return entries
        .map((entry) => ({
            ...entry,
            isDuplicate: isDuplicateSlot(twice, entry.number, entry.partLabel)
        }))
        .sort(byNumber);
}

/**
 * The Planum and Profil descriptions of one Befund as a grouped list — per kind
 * a header (label + count + an "Add" button) and rows that open the editor on
 * click. Shared by the field Befunde list and the office Befund dialog's
 * "Beschreibungen" section, so the two look identical.
 *
 * The host supplies the route `basePath` and the `onNavigate` used to reach an
 * editor — plain in the field, unsaved-changes-guarded in the office dialog.
 */
export default function FindingDescriptionList({
    projectId,
    planums,
    profiles,
    basePath,
    onNavigate,
    allowPlanums = true
}: {
    projectId: string;
    planums: FindingPlanumDocType[];
    profiles: FindingProfileDocType[];
    /** Route base for the editors, e.g. `/office/projects/<p>/findings/<f>`. */
    basePath: string;
    /** Navigate to an editor route (guarded in the office dialog). */
    onNavigate: (to: string) => void;
    /**
     * False for a Befund flagged "nur im Profil sichtbar": it has no Planum by
     * definition, so the add button for that block is withheld. Existing ones
     * stay listed and editable — a record could still arrive that way from an
     * import.
     */
    allowPlanums?: boolean;
}) {
    const intl = useIntl();
    const ability = useAbility();

    const blocks = useMemo<KindBlock[]>(
        () => [
            {
                kind: 'planum',
                segment: 'planums',
                subject: 'FindingPlanum',
                titleId: 'planum.title',
                deleteTitleId: 'planum.delete.title',
                deleteConfirmId: 'planum.delete.confirm',
                addId: 'planum.add',
                emptyId: 'planum.empty',
                labelId: 'planum.label',
                icon: <LayersOutlinedIcon fontSize="small" />,
                rows: describedRows(
                    planums.map((planum) => ({
                        id: planum.id,
                        number: planum.planumNumber,
                        partLabel: planum.partLabel
                    }))
                )
            },
            {
                kind: 'profile',
                segment: 'profiles',
                subject: 'FindingProfile',
                titleId: 'profile.title',
                deleteTitleId: 'profile.delete.title',
                deleteConfirmId: 'profile.delete.confirm',
                addId: 'profile.add',
                emptyId: 'profile.empty',
                labelId: 'profile.label',
                // Flipped 180° so the "terrain" points down — reads as a
                // section/cut going into the ground rather than a hill.
                icon: (
                    <TerrainOutlinedIcon
                        fontSize="small"
                        sx={{ transform: 'rotate(180deg)' }}
                    />
                ),
                rows: describedRows(
                    profiles.map((profile) => ({
                        id: profile.id,
                        number: profile.profileNumber,
                        partLabel: profile.partLabel
                    }))
                )
            }
        ],
        [planums, profiles]
    );

    // The description the delete button asked about — nothing is removed until
    // the dialog is confirmed. Deleted through useFindingDeletion, so the
    // references to it go too and the dialog can say where.
    const [deleteTarget, setDeleteTarget] = useState<{
        block: KindBlock;
        id: string;
    } | null>(null);
    const deletion = useFindingDeletion(
        projectId,
        deleteTarget
            ? { kind: deleteTarget.block.kind, id: deleteTarget.id }
            : null
    );

    async function confirmDelete() {
        await deletion.remove();
        setDeleteTarget(null);
    }

    return (
        <>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    columnGap: 3,
                    rowGap: 2,
                    // A vertical rule down the middle of the column gap (sm+ only,
                    // where the two columns sit side by side).
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        display: { xs: 'none', sm: 'block' },
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: '50%',
                        borderLeft: 1,
                        borderColor: 'divider'
                    }
                }}
            >
                {blocks.map((block) => {
                    const canEdit =
                        ability.can(
                            'create',
                            scopedSubject(block.subject, { projectId })
                        ) &&
                        (allowPlanums || block.segment !== 'planums');
                    const canDelete = ability.can(
                        'delete',
                        scopedSubject(block.subject, { projectId })
                    );
                    const deleteLabel = intl.formatMessage({
                        id: 'common.delete'
                    });
                    const typeLabel = intl.formatMessage({ id: block.titleId });
                    const addLabel = intl.formatMessage({ id: block.addId });
                    return (
                        <Box key={block.segment}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    minHeight: 32
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {block.rows.length > 0
                                        ? `${typeLabel} (${block.rows.length})`
                                        : typeLabel}
                                </Typography>
                                {canEdit && (
                                    <Tooltip title={addLabel}>
                                        <IconButton
                                            size="small"
                                            aria-label={addLabel}
                                            sx={addOutlineSx}
                                            component={RouterLink}
                                            to={`${basePath}/${block.segment}/new`}
                                            onClick={linkClick(() =>
                                                onNavigate(
                                                    `${basePath}/${block.segment}/new`
                                                )
                                            )}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            {block.rows.length === 0 ? (
                                <Typography
                                    variant="body2"
                                    sx={{ color: 'text.secondary' }}
                                >
                                    <FormattedMessage id={block.emptyId} />
                                </Typography>
                            ) : (
                                // Clear of the heading row: a row's hover tint
                                // reaches full width, and flush it would touch
                                // the add button above it.
                                <List dense disablePadding sx={{ mt: 0.5 }}>
                                    {block.rows.map((row) => (
                                        <ListItem
                                            key={row.id}
                                            disablePadding
                                            // Beside the row's link, not inside
                                            // it: a click here must not open the
                                            // editor.
                                            secondaryAction={
                                                canDelete && (
                                                    <Tooltip
                                                        title={deleteLabel}
                                                    >
                                                        <IconButton
                                                            edge="end"
                                                            size="small"
                                                            color="error"
                                                            aria-label={
                                                                deleteLabel
                                                            }
                                                            onClick={() =>
                                                                setDeleteTarget(
                                                                    {
                                                                        block,
                                                                        id: row.id
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )
                                            }
                                        >
                                            <ListItemButton
                                                disableGutters
                                                component={RouterLink}
                                                to={`${basePath}/${block.segment}/${row.id}/edit`}
                                                onClick={linkClick(() =>
                                                    onNavigate(
                                                        `${basePath}/${block.segment}/${row.id}/edit`
                                                    )
                                                )}
                                                sx={{ borderRadius: 1, px: 1 }}
                                            >
                                                <ListItemIcon
                                                    sx={{ minWidth: 32 }}
                                                >
                                                    {block.icon}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={intl.formatMessage(
                                                        { id: block.labelId },
                                                        {
                                                            number: [
                                                                row.number ||
                                                                    '?',
                                                                // Each Teil is its
                                                                // own row, so it has
                                                                // to say which one.
                                                                row.partLabel.trim()
                                                                    ? intl.formatMessage(
                                                                          {
                                                                              id: 'description.parts.one'
                                                                          },
                                                                          {
                                                                              label: row.partLabel.trim()
                                                                          }
                                                                      )
                                                                    : ''
                                                            ]
                                                                .filter(Boolean)
                                                                .join(', ')
                                                        }
                                                    )}
                                                />
                                                {row.isDuplicate && (
                                                    <Tooltip
                                                        title={intl.formatMessage(
                                                            {
                                                                id: 'description.duplicate.tooltip'
                                                            }
                                                        )}
                                                    >
                                                        <WarningAmberIcon
                                                            color="warning"
                                                            fontSize="small"
                                                        />
                                                    </Tooltip>
                                                )}
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    );
                })}
            </Box>

            {/* One dialog per kind, so its text stays put while it fades out
                after the target is cleared. */}
            {blocks.map((block) => (
                <ConfirmDialog
                    key={block.kind}
                    open={deleteTarget?.block.kind === block.kind}
                    title={<FormattedMessage id={block.deleteTitleId} />}
                    message={<FormattedMessage id={block.deleteConfirmId} />}
                    details={<DeletionImpactDetails impact={deletion.impact} />}
                    confirmColor="error"
                    onConfirm={confirmDelete}
                    onCancel={() => setDeleteTarget(null)}
                />
            ))}
        </>
    );
}
